/* The post-extras "take another design" offer (config.bagDesigns, bag
   products only).

   By this point the customer has told us everything: the pets, the photos,
   the names, how many. A second bag in a different design needs none of it
   again — only its colour. So the offer is one tap plus one swatch, at the
   20% the store's automatic discount really applies to a second bag.

   The list comes from the collection at render time
   (personalizer-bag-designs-json.liquid), so a new design joins the offer by
   joining the collection.

   Prices shown here are display only; the automatic discount in Shopify is
   what charges less. Both read the same percent from the config, so the card
   and the receipt cannot disagree.

   Markup lives in personalizer-bag-designs-render.js. */
(function () {
  'use strict';

  var ctx = null;
  var offer = null;
  var pick = { handle: null, color: null };
  var built = false;

  function money(cents) {
    return window.PersonalizerCart.formatMoney(cents, ctx.config.moneyFormat);
  }

  function designs() { return (offer && offer.items) || []; }

  function byHandle(handle) {
    var found = null;
    designs().forEach(function (d) { if (d.handle === handle) found = d; });
    return found;
  }

  /* The chosen design's variant for THIS order — same order type, same pet
     count. Its SKU grammar is the shared one, so the lookup is the shared one
     too, fed the other product's variants.

     A second bag has its OWN variant (…-2ND), priced identically to the
     normal one. That is the only thing telling Shopify's discount engine
     which of two matching bags is the added one: without it the engine sees
     two identical lines and puts the 20% on whichever it likes, which is how
     the main bag ended up discounted instead of the one just chosen.

     No fallback to the plain variant on purpose. The store's discount is
     scoped to the -2ND variants alone, so a design offered on its plain one
     would advertise 20% and then charge full price at checkout. A design
     without its second-bag variant drops out of the offer instead (see
     sellable) — missing beats mispriced. */
  function variantFor(design) {
    if (!design) return null;
    var normal = window.PersonalizerCart.oneBagComboVariant(
      { variants: design.variants, skuPrefix: design.skuPrefix },
      ctx.state.orderType || 'full',
      ctx.state.besties.length || 1,
      false);
    if (!normal) return null;
    var second = null;
    (design.variants || []).forEach(function (v) {
      if (v.sku === normal.sku + '-2ND' && v.available) second = v;
    });
    return second;
  }

  function fullCents(design) {
    var v = variantFor(design);
    return v ? v.price : 0;
  }

  function nowCents(design) {
    return Math.round(fullCents(design) * (100 - (offer.discountPct || 20)) / 100);
  }

  /* A design the customer cannot actually receive in this shape (no matching
     variant, or sold out) is not offered at all — better absent than broken. */
  function sellable(design) {
    var v = variantFor(design);
    return !!(v && v.available);
  }

  function live() { return designs().filter(sellable); }

  /* Only the one-pet Full Set is offered a second design. Bigger orders are
     already a big ask, and a tote-only or charm-only order is not the customer
     this pitch is written for — "another bag" means another bag like the one
     they just built. */
  function fitsOffer() {
    return (ctx.state.orderType || 'full') === 'full' && ctx.state.besties.length === 1;
  }

  function hasOffer() {
    return !!(offer && ctx && ctx.config.bagMode === 'oneBag' && fitsOffer() && live().length);
  }

  function chosen() { return pick.handle ? byHandle(pick.handle) : null; }
  function accepted() { return !!chosen(); }

  /* Charm-only orders have no bag, so there is no colour to match. */
  function needsColour() { return (ctx.state.orderType || 'full') !== 'charm'; }

  function missing() {
    var d = chosen();
    if (!d) return null;
    if (needsColour() && !pick.color) return 'Pick a colour for the ' + d.title + '.';
    return null;
  }
  function isValid() { return !missing(); }

  function cents() { return accepted() ? nowCents(chosen()) : 0; }
  function compareCents() { return accepted() ? fullCents(chosen()) : 0; }

  /* One extra cart line: the other design's variant, carrying the SAME pets
     the main line describes, in its own colour. Photos are attached by
     personalizer-cart.js, which owns the upload group. */
  function items() {
    var d = chosen();
    if (!d) return [];
    var v = variantFor(d);
    if (!v) return [];
    var ot = ctx.state.orderType || 'full';
    var made = window.PersonalizerCart.oneBagProps(ctx, ot, pick.color);
    if (made.error) return [];
    var props = made.props;
    props['_personalizer'] = 'v4';
    return [{ id: v.id, quantity: 1, properties: props, reusesOrderPhotos: true }];
  }

  /* --- DOM --- */

  function mountEl() { return ctx.root.querySelector('[data-personalizer-bagdesigns]'); }

  function viewFor(d) {
    return {
      handle: d.handle,
      title: d.title,
      tagline: d.tagline || '',
      url: d.url,
      image: d.image,
      palettes: d.palettes || [],
      fullCents: fullCents(d),
      nowCents: nowCents(d),
      on: pick.handle === d.handle,
      color: pick.handle === d.handle ? pick.color : null,
      needsColour: needsColour(),
    };
  }

  function sync() {
    var mount = mountEl();
    if (!mount) return;
    window.PersonalizerBagDesignsRender.sync(mount, live().map(viewFor), money);
  }

  function onClick(e) {
    if (!ctx || !e.target.closest) return;
    var card = e.target.closest('[data-pzd-pick]');
    if (card && mountEl() && mountEl().contains(card)) {
      var handle = card.getAttribute('data-pzd-pick');
      // Tapping the chosen design again backs out of the offer entirely.
      if (pick.handle === handle) { pick.handle = null; pick.color = null; }
      else { pick.handle = handle; pick.color = null; }
      sync();
      ctx.onStateChange();
      return;
    }
    var dot = e.target.closest('[data-pzd-color]');
    if (dot && mountEl() && mountEl().contains(dot)) {
      pick.color = dot.getAttribute('data-pzd-color');
      sync();
      ctx.onStateChange();
    }
  }

  function init(context) {
    ctx = context;
    offer = ctx.config.bagDesigns;
    // Wired on the config alone, not on hasOffer(): the order can still change
    // shape into (or out of) the one this offer is for, and a listener that
    // was never attached cannot be attached later.
    if (!offer || ctx.config.bagMode !== 'oneBag') return;
    document.addEventListener('click', onClick, false);
  }

  /* Called when the interstitial becomes visible. */
  function render(context) {
    ctx = context;
    if (!hasOffer()) return;
    var mount = mountEl();
    if (!mount) return;
    if (!built) {
      mount.innerHTML = window.PersonalizerBagDesignsRender.list(offer, live().map(viewFor), money);
      built = true;
    }
    mount.hidden = false;
    sync();
  }

  window.PersonalizerBagDesigns = {
    init: init,
    render: render,
    hasOffer: hasOffer,
    accepted: accepted,
    isValid: isValid,
    missing: missing,
    cents: cents,
    compareCents: compareCents,
    items: items,
  };
})();
